export interface ImageAttribution {
  unsplashId?: string;
  unsplashAuthor?: string;
  unsplashAuthorUrl?: string;
}

// Appends UTM params (per Unsplash API guidelines) so the photographer
// receives referral credit when someone follows the attribution link.
const UTM = '?utm_source=kulalearn&utm_medium=referral';

const OVERLAY_CLASSES =
  'absolute bottom-2 right-2 z-10 px-2 py-1 rounded bg-black/50 text-white text-[11px] leading-tight backdrop-blur-sm pointer-events-auto';
const INLINE_CLASSES = 'text-xs text-gray-500';

interface UnsplashCreditProps {
  author?: string;
  authorUrl?: string;
  variant?: 'overlay' | 'inline';
  className?: string;
}

// https://help.unsplash.com/en/articles/2511315-guideline-attribution
export function UnsplashCredit({
  author,
  authorUrl,
  variant = 'overlay',
  className = '',
}: UnsplashCreditProps) {
  if (!author) return null;

  const base = variant === 'overlay' ? OVERLAY_CLASSES : INLINE_CLASSES;

  return (
    <p className={`${base} ${className}`.trim()}>
      Photo by{' '}
      <a
        href={authorUrl ? `${authorUrl}${UTM}` : undefined}
        target="_blank"
        rel="noopener noreferrer"
        className="underline hover:opacity-80"
        onClick={(e) => e.stopPropagation()}
      >
        {author}
      </a>{' '}
      on{' '}
      <a
        href={`https://unsplash.com${UTM}`}
        target="_blank"
        rel="noopener noreferrer"
        className="underline hover:opacity-80"
        onClick={(e) => e.stopPropagation()}
      >
        Unsplash
      </a>
    </p>
  );
}

// Merge new attribution (or lack thereof) into slide data. Passing `undefined`
// clears any prior attribution — used when removing or replacing an image.
export function withImageAttribution<T extends ImageAttribution>(
  data: T,
  attribution: ImageAttribution | undefined,
): T {
  return {
    ...data,
    unsplashId: attribution?.unsplashId,
    unsplashAuthor: attribution?.unsplashAuthor,
    unsplashAuthorUrl: attribution?.unsplashAuthorUrl,
  };
}
